/** TanStack Query hooks for admin user management (CRUD, ban, password, email). */
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/shared/api/client';
import type { User, CreateUserInput, UpdateUserInput, PaginatedResponse } from '@formency/shared';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface UserWithAuth extends User {
  emailConfirmedAt: string | null;
  lastSignInAt: string | null;
  invitedAt: string | null;
  bannedUntil: string | null;
  /** Invite codes the user can still mint. Refilled by admin. */
  inviteBudget: number;
}

export interface AdminUserListParams {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  roleFilter?: string;
  searchQuery?: string;
}

// ─── Query key factory ──────────────────────────────────────────────────────

export const adminUserKeys = {
  all: ['f', 'admin-users'] as const,
  list: (params: AdminUserListParams) => [...adminUserKeys.all, 'list', params] as const,
  detail: (userId: string) => [...adminUserKeys.all, userId] as const,
  activity: (userId: string, params?: { page?: number; pageSize?: number }) =>
    [...adminUserKeys.all, userId, 'activity', params] as const,
  enrolments: (userId: string, params?: { page?: number; pageSize?: number }) =>
    [...adminUserKeys.all, userId, 'enrolments', params] as const,
};

// ─── Queries ────────────────────────────────────────────────────────────────

/** Fetch paginated list of users with sorting and filtering. */
export function useAdminUsers(params: AdminUserListParams = {}) {
  return useQuery<PaginatedResponse<User>>({
    queryKey: adminUserKeys.list(params),
    queryFn: () => api.get<PaginatedResponse<User>>('/users', params as Record<string, unknown>),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

/** Fetch a single user by ID (includes auth info). */
export function useAdminUser(userId: string | undefined) {
  return useQuery<UserWithAuth>({
    queryKey: adminUserKeys.detail(userId!),
    queryFn: () => api.get<UserWithAuth>(`/users/${userId}`),
    enabled: !!userId,
    staleTime: 5 * 60_000,
  });
}

/** Fetch paginated activity for a user. */
export function useAdminUserActivity(
  userId: string,
  params?: { page?: number; pageSize?: number }
) {
  return useQuery({
    queryKey: adminUserKeys.activity(userId, params),
    queryFn: () =>
      api.get(`/users/${userId}/activity`, params as Record<string, unknown> | undefined),
    enabled: !!userId,
  });
}

// ─── Mutations ──────────────────────────────────────────────────────────────

/** Create a new user. */
export function useCreateAdminUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateUserInput) => api.post<User>('/users', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminUserKeys.all });
      toast.success('User created');
    },
    onError: () => {
      toast.error('Failed to create user');
    },
  });
}

/** Update a user's profile. */
export function useUpdateAdminUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: UpdateUserInput }) =>
      api.put<User>(`/users/${userId}`, data),
    onSuccess: (_data, { userId }) => {
      qc.invalidateQueries({ queryKey: adminUserKeys.detail(userId) });
      qc.invalidateQueries({ queryKey: adminUserKeys.all });
      toast.success('User updated');
    },
    onError: () => {
      toast.error('Failed to update user');
    },
  });
}

/** Delete a user. */
export function useDeleteAdminUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.del(`/users/${userId}`),
    onSuccess: (_data, userId) => {
      // Remove the deleted user's detail cache entirely so any in-flight
      // useAdminUser(userId) doesn't refetch a 404 (the row is gone) —
      // that produced a confusing "User not found" toast on top of the
      // success message. Sub-queries under the detail key (sessions,
      // activity) are also removed by the prefix match.
      qc.removeQueries({ queryKey: adminUserKeys.detail(userId) });
      // List queries DO need to refetch — the deleted user should drop
      // out of the rows. The 'list' prefix matches every paginated/filtered
      // variant the table is using.
      qc.invalidateQueries({ queryKey: [...adminUserKeys.all, 'list'] });
      toast.success('User deleted');
    },
    onError: () => {
      toast.error('Failed to delete user');
    },
  });
}

/** Reset a user's password (generates a recovery link). */
export function useResetAdminUserPassword() {
  return useMutation({
    mutationFn: (userId: string) =>
      api.post<{ actionLink: string }>(`/users/${userId}/reset-password`),
    onError: () => {
      toast.error('Failed to generate reset link');
    },
  });
}

/** Change a user's password directly. */
export function useChangeAdminUserPassword() {
  return useMutation({
    mutationFn: ({ userId, password }: { userId: string; password: string }) =>
      api.post(`/users/${userId}/change-password`, { password }),
    onSuccess: () => {
      toast.success('Password changed');
    },
    onError: () => {
      toast.error('Failed to change password');
    },
  });
}

/** Ban a user for a specified duration. */
export function useBanAdminUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, duration }: { userId: string; duration: string }) =>
      api.post(`/users/${userId}/ban`, { duration }),
    onSuccess: (_data, { userId }) => {
      qc.invalidateQueries({ queryKey: adminUserKeys.detail(userId) });
      qc.invalidateQueries({ queryKey: adminUserKeys.all });
      toast.success('User banned');
    },
    onError: () => {
      toast.error('Failed to ban user');
    },
  });
}

/** Unban a user. */
export function useUnbanAdminUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.post(`/users/${userId}/unban`),
    onSuccess: (_data, userId) => {
      qc.invalidateQueries({ queryKey: adminUserKeys.detail(userId) });
      qc.invalidateQueries({ queryKey: adminUserKeys.all });
      toast.success('User unbanned');
    },
    onError: () => {
      toast.error('Failed to unban user');
    },
  });
}

/** Confirm a user's email address. */
export function useConfirmAdminUserEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.post(`/users/${userId}/confirm-email`),
    onSuccess: (_data, userId) => {
      qc.invalidateQueries({ queryKey: adminUserKeys.detail(userId) });
      toast.success('Email confirmed');
    },
    onError: () => {
      toast.error('Failed to confirm email');
    },
  });
}

/** Reset a user's avatar. */
export function useResetAdminUserAvatar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.del(`/users/${userId}/avatar`),
    onSuccess: (_data, userId) => {
      qc.invalidateQueries({ queryKey: adminUserKeys.detail(userId) });
      qc.invalidateQueries({ queryKey: adminUserKeys.all });
      toast.success('Avatar reset');
    },
    onError: () => {
      toast.error('Failed to reset avatar');
    },
  });
}

// ─── Sessions ────────────────────────────────────────────────────────────────

export interface AdminUserSession {
  id: string;
  createdAt: string;
  updatedAt: string;
  expiresAt: string;
  ipAddress: string | null;
  userAgent: string | null;
  /** Set when this session was created via admin impersonation. */
  impersonatedBy: string | null;
}

/** List a user's active BetterAuth sessions. */
export function useAdminUserSessions(userId: string | undefined) {
  return useQuery<AdminUserSession[]>({
    queryKey: [...adminUserKeys.detail(userId ?? ''), 'sessions'],
    queryFn: () => api.get<AdminUserSession[]>(`/users/${userId}/sessions`),
    enabled: !!userId,
  });
}

/** Set a user's invite budget (admin top-up). */
export function useSetAdminUserInviteBudget() {
  const qc = useQueryClient();
  return useMutation<void, Error, { userId: string; budget: number }>({
    mutationFn: ({ userId, budget }) =>
      api.patch(`/admin/users/${userId}/invite-budget`, { budget }),
    onSuccess: (_data, { userId }) => {
      qc.invalidateQueries({ queryKey: adminUserKeys.detail(userId) });
      toast.success('Invite budget updated');
    },
    onError: () => {
      toast.error('Failed to update invite budget');
    },
  });
}

/**
 * Bulk floor: ensure every user has at least `floor` invites. Idempotent —
 * users already at or above the floor are left alone, only those below it
 * get raised. Returns the count of users actually bumped so the UI can
 * report "Topped up N users".
 */
export function useSetInviteFloor() {
  const qc = useQueryClient();
  return useMutation<{ floor: number; affected: number }, Error, { floor: number }>({
    mutationFn: ({ floor }) =>
      api.post<{ floor: number; affected: number }>('/admin/users/invite-budget/set-floor', {
        floor,
      }),
    onSuccess: ({ affected, floor }) => {
      // Cascade: every detail and list cache potentially has a stale budget.
      qc.invalidateQueries({ queryKey: adminUserKeys.all });
      if (affected === 0) {
        toast.success(`Everyone already has at least ${floor} invites`);
      } else {
        toast.success(
          `Topped up ${affected} ${affected === 1 ? 'user' : 'users'} to ${floor} invites`
        );
      }
    },
    onError: () => {
      toast.error('Failed to set invite floor');
    },
  });
}

/** Revoke a single session by row id. */
export function useRevokeAdminUserSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, sessionId }: { userId: string; sessionId: string }) =>
      api.del(`/users/${userId}/sessions/${encodeURIComponent(sessionId)}`),
    onSuccess: (_data, { userId }) => {
      qc.invalidateQueries({ queryKey: [...adminUserKeys.detail(userId), 'sessions'] });
      toast.success('Session revoked');
    },
    onError: () => {
      toast.error('Failed to revoke session');
    },
  });
}
